import {createAsyncThunk} from "@reduxjs/toolkit";
import {HOSTNAME} from "../configs/Domain";
import {getValuesByPaths, prepareJsonPayload} from "../store/utils";
import {deepObjectFromDotPath} from "../utils/tools";
import {handleError} from "./_apiUtils";

const apiRequest = async (url, method, data) => {
  let payload = { method }
  if (method !== 'GET'){
      payload = prepareJsonPayload({ method, data });
  }
  const response = await fetch(url, prepareJsonPayload(payload));
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || `Failed to ${method} data`);
  }
  return response.json();
};



export const deleteUserAppData = createAsyncThunk(
  'appData/deleteUserAppData',
  async (ids, { rejectWithValue }) => {
    try {
      await apiRequest(`${HOSTNAME}/user/app_data/delete`, "DELETE", ids);
      return ids;
    } catch (error) {
      return handleError(error, rejectWithValue);
    }
  }
);

export const createUserAppData = createAsyncThunk(
  'user/createUserAppData',
  async ({ appType, title, data }, { rejectWithValue }) => {
    try {
      const url = `${HOSTNAME}/user/app_data/create`;
      const body = { app_type: appType, title, data };
      const responseData = await apiRequest(url, "POST", body);
      return { data: responseData };
    } catch (error) {
      return handleError(error, rejectWithValue);
    }
  }
);

export const updateUserAppData = createAsyncThunk(
  'user/updateUserAppData',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const url = `${HOSTNAME}/user/app_data/${id}/update`;
      const responseData = await apiRequest(url, "PUT", { data });
      return { id, data };
    } catch (error) {
      return handleError(error, rejectWithValue);
    }
  }
);

export const saveUserAppData = createAsyncThunk(
  'user/saveUserAppData',
  async ({ appType, title, data, id }, { dispatch, rejectWithValue }) => {
    try {
      if (id) {
        return await dispatch(updateUserAppData({ id, data })).unwrap();
      } else {
        return await dispatch(createUserAppData({ appType, title, data })).unwrap();
      }
    } catch (error) {
      return handleError(error, rejectWithValue);
    }
  }
);

export const updateUser = createAsyncThunk(
  'user/updateUser',
  async ({ updateData, match }, { getState, rejectWithValue }) => {
      const userId = localStorage.getItem('uid');

      const payload = {
        match: match ? match : {uid: userId},
        update: updateData
      }
      try {
          const response = await fetch(`${HOSTNAME}/user/update`, prepareJsonPayload({data: payload}));

          if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update user data');
          }

        const responseData = await response.json();
        return {responseData, updateData}; // This is the updated user data from the server
        } catch (error) {
        return rejectWithValue(error.message);
        }
    });


/**
 * Specific implementation of updateUser specific to setting and returning data that may be dotpath nested etc
 * and being able to correctly update this within the redux state
 */
export const setUserFields = createAsyncThunk(
  'user/setUserData',
  async ({ changedFields, match }, { getState, rejectWithValue }) => {
      const userId = localStorage.getItem('uid');

      const payload = {
        match: match ? match : {uid: userId},
        update: {
            $set: changedFields
        }
      }
      try {
          const response = await fetch(`${HOSTNAME}/user/update`, prepareJsonPayload({data: payload}));
          if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update user data');
          }
          const responseData = await response.json();
          return deepObjectFromDotPath(changedFields); // This is the updated user data from the server
        } catch (error) {
        return rejectWithValue(error.message);
        }
    });

export const fetchUserFields = createAsyncThunk(
  'user/fetchUserFields',
  async (keyPaths, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const existingValues = getValuesByPaths(state.user.data, keyPaths);
      const missingKeys = keyPaths.filter((key, index) => !existingValues[index]);

      if (missingKeys.length === 0) {
        // All fields exist, no need to fetch anything
        return;
      }

      const response = await fetch(`${HOSTNAME}/user/fetch?fields=${missingKeys.join(',')}`, prepareJsonPayload({
        method: "GET"
      }));

      if (!response.ok) {
        throw new Error('Failed to fetch user fields');
      }

      const responseData = await response.json();
      return responseData; // Should match the structure of the missing fields
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const createProspect = createAsyncThunk(
  'user/createProspect',
  async ({ name, email, phone }, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await fetch(`${HOSTNAME}/prospects/create`, prepareJsonPayload({
        data: { name, email, phone },
        method: "POST"
      }));

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create prospect');
      }

      const prospectData = await response.json(); // Assuming this includes the new prospect's userId
      return prospectData; // Return the new prospect data for further use
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProspectPortfolios = createAsyncThunk(
    'user/updateProspectPortfolios',
    async ({ prospectUserId, portfolios }, { getState, rejectWithValue }) => {
        try {
            const response = await fetch(`${HOSTNAME}/prospects/${prospectUserId}/portfolios/update`,
                prepareJsonPayload({data: portfolios, method: "PUT"}))
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update prospect portfolios');
            }

            const data = await response.json();
            return { prospectUserId, portfolios: data.portfolios };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);



export const createPage = createAsyncThunk(
  'user/createPage',
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${HOSTNAME}/pages/create`, prepareJsonPayload({
        data,
        method: 'POST',
      }));

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create page');
      }

      const pageData = await response.json();
      return pageData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePage = createAsyncThunk(
  'user/updatePage',
  async ({ pageId, data }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${HOSTNAME}/pages/update/${pageId}`, prepareJsonPayload({
        data: data,
        method: 'PUT',
      }));

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update page');
      }

      const pageData = await response.json();
      return {pageId, pageData};
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const deletePages = createAsyncThunk(
  'user/deletePages',
  async ({ pageIds }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${HOSTNAME}/pages/delete`, prepareJsonPayload({
        method: 'DELETE',
        data: pageIds,
      }));

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete pages');
      }
      return pageIds;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const listPageViews = createAsyncThunk(
  'user/listPageViews',
  async ({ pageId }, { getState, rejectWithValue }) => {
    const state = getState();

    // Check if the page views for this pageId are already fetched
    const pageDetails = state.user.data?.page_details;
    if (pageDetails && pageDetails.some(page => page.page_id === pageId && page.pageViews)) {
      return { pageId, pageViews: pageDetails.find(page => page.page_id === pageId).pageViews }; // Return existing views if already fetched
    }

    try {
      const response = await fetch(`${HOSTNAME}/pages/${pageId}/list/views`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch page views');
      }

      const pageViews = await response.json();
      return { pageId, pageViews };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const resetPassword = createAsyncThunk(
    'user/resetPassword',
    async ({ password }, { rejectWithValue }) => {
        try {
        const response = await fetch(`${HOSTNAME}/user/reset/password`, prepareJsonPayload({
            data: { password },
            method: 'POST',
        }));

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to reset password');
        }

        return;
        } catch (error) {
        return rejectWithValue(error.message);
        }
    }

)


export const createTouchpoint = createAsyncThunk(
  'touchpoint/create',
  async ({ data }, { rejectWithValue }) => {
    try {
      const touchpointData = await apiRequest(`${HOSTNAME}/touchpoints/create`, "POST", data);
      return touchpointData;
    } catch (error) {
      return handleError(error, { rejectWithValue });
    }
  }
);



export const updateTouchpoint = createAsyncThunk(
  'touchpoint/update',
  async ({ touchpointId, data }, { rejectWithValue }) => {
    try {
      const touchpointData = await apiRequest(
        `${HOSTNAME}/touchpoints/update/${touchpointId}`,
        "PUT",
        data
      );
      return { touchpointId, touchpointData };
    } catch (error) {
      return handleError(error, { rejectWithValue });
    }
  }
);

export const deleteTouchpoint = createAsyncThunk(
  'touchpoint/delete',
  async ({ touchpointId }, { rejectWithValue }) => {
    try {
      await apiRequest(`${HOSTNAME}/touchpoints/delete/${touchpointId}`, "DELETE");
      return touchpointId;
    } catch (error) {
      return handleError(error, { rejectWithValue });
    }
  }
);

export const fetchTouchpoint = createAsyncThunk(
  'touchpoint/fetch',
  async (touchpointId , { rejectWithValue }) => {
    try {
      const touchpoint = await apiRequest(
        `${HOSTNAME}/touchpoints/fetch/${touchpointId}`,
        "GET"
      );
      return touchpoint;
    } catch (error) {
      return handleError(error, { rejectWithValue });
    }
  }
);

export const fetchTouchpoints = createAsyncThunk(
  'touchpoint/fetchTouchpoints',
  async (touchpointIds , { rejectWithValue }) => {
    try {
      const touchpoints = await apiRequest(
        `${HOSTNAME}/touchpoints/fetch/many?ids=${touchpointIds.join(',')}`,
        "GET"
      );
      return touchpoints;
    } catch (error) {
      return handleError(error, rejectWithValue);
    }
  }
);


export const createOrUpdatePortfolioGroup = createAsyncThunk(
  'user/createOrUpdatePortfolioGroup',
  async ({ group_id, portfolio_ids, name }, { rejectWithValue }) => {
    try {
      const payload = {
        ...(group_id && { group_id }), // Only include group_id if it exists
        portfolio_ids,
        name
      };

      const responseData = await fetch(
        `${HOSTNAME}/user/groups/update`, prepareJsonPayload({ data: payload, method: "POST" }));

      return {
          group_id,
          portfolio_ids,
            name
      }; // Will include the group_id, portfolio_ids, and name
    }
    catch (error) {
      return handleError(error, rejectWithValue );
    }
  }
);
